import React from 'react'

import T from 'components/FormattedMessage'
import UserInfo from 'components/UserInfo'
import VitalStatistics from 'components/VitalStatistics'
import MostSelected from 'components/MostSelected'
import Button from 'components/Button'
import Layer from 'components/Layer'
import NavigationHeader from 'components/NavigationHeader'

import { Typography, Grid, withMobileDialog, CircularProgress } from '@material-ui/core'

import { UserRelationship, GeneralResponse, UserInfoResponse } from 'protos'
import { Action } from 'types'

interface Props {
  isSelf: boolean
  onClose: () => void
  onChallenge: () => void
  getUserInfo: () => Promise<UserInfoResponse>
  addFriend: () => Promise<GeneralResponse>
  removeFriend: () => Promise<GeneralResponse>
}

interface State {
  userInfo: Action<UserInfoResponse | null>
  friend: Action<null>
}

type InjectedProps = Props & { fullScreen: boolean }

class UserLayer extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      userInfo: { isLoading: true },
      friend: { isLoading: false },
    }
  }

  componentDidMount() {
    this.getUserInfo()
  }

  private getUserInfo = () => {
    this.props.getUserInfo()
      .then((result: UserInfoResponse) => {
        this.setState({ userInfo: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ userInfo: { isLoading: false, errorCode: err.message } })
      })
  }

  private changeRelationship = (fn: () => Promise<GeneralResponse>) =>
    () => {
      this.setState(
        { friend: { isLoading: true } },
        () => {
          fn()
            .then(() => {
              this.setState(state => ({
                friend: { isLoading: false },
                userInfo: { ...state.userInfo, isLoading: true, errorCode: undefined },
              }))
              this.getUserInfo()
            })
            .catch((err: Error) => {
              this.setState({ friend: { isLoading: false, errorCode: err.message } })
            })
        },
      )
    }

  private addAsFriend = this.changeRelationship(this.props.addFriend)

  private removeAsFriend = this.changeRelationship(this.props.removeFriend)

  private renderActions = (relationship: UserRelationship) => {
    if (this.state.friend.isLoading || this.state.userInfo.isLoading) {
      return <div style={{ textAlign: 'center' }}><CircularProgress size={50} thickness={7} /></div>
    }

    switch (relationship) {
      case UserRelationship.UR_NONE:
        return (
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={this.addAsFriend}
          >
            <T id="userprofile_action_addasfriend" />
          </Button>
        )
      case UserRelationship.UR_OUTGOING:
        return (
          <>
            <Typography variant="body1" align="center">
              <T id="userprofile_outgoingrelationshipinfo" />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={this.addAsFriend}
                >
                  <T id="userprofile_action_acceptfriendrequest" />
                </Button>
              </Grid>
              <Grid item xs>
                <Button
                  fullWidth
                  color="dangerous"
                  variant="contained"
                  onClick={this.removeAsFriend}
                >
                  <T id="userprofile_action_rejectfriendrequest" />
                </Button>
              </Grid>
            </Grid>
          </>
        )
      case UserRelationship.UR_INCOMING:
        return (
          <>
            <Typography variant="body1" align="center">
              <T id="userprofile_incomingrelationshipinfo" />
            </Typography>
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              onClick={this.removeAsFriend}
            >
              <T id="userprofile_action_undofriendrequest" />
            </Button>
          </>
        )
      case UserRelationship.UR_MUTUAL:
        return (
          <Grid container spacing={2}>
            <Grid item xs>
              <Button
                fullWidth
                color="dangerous"
                variant="contained"
                onClick={this.removeAsFriend}
              >
                <T id="userprofile_action_removefriend" />
              </Button>
            </Grid>
            <Grid item xs>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                onClick={this.props.onChallenge}
              >
                <T id="friends_action_challenge" />
              </Button>
            </Grid>
          </Grid>
        )
      default:
        return null
    }
  }

  render() {
    const { onClose, fullScreen, isSelf } = this.props
    const { result: userInfo } = this.state.userInfo

    if (!userInfo || !userInfo.user) {
      return null
    }

    return (
      <Layer
        open
        maxWidth="sm"
        onClose={onClose}
        fullScreen={fullScreen}
        width={fullScreen ? '100%' : undefined}
      >
        <NavigationHeader
          title={<T id="userprofile_title" />}
          onBackClick={onClose}
          style={{ backgroundColor: '#fff' }}
        />
        <UserInfo
          avatarUrl={userInfo.user.photoUrl}
          username={userInfo.user.name}
          playedGames={userInfo.gamesTotal}
          winnings={userInfo.user.balance}
          wins={userInfo.gamesWon}
        />
        {!isSelf && (
          <div style={{ padding: 24 }}>
            {this.renderActions(userInfo.user.relationship!)}
          </div>
        )}
        <VitalStatistics
          topPlayerScore={userInfo.topPlayerScore}
          topTeamScore={userInfo.topTeamScore}
        />
        <MostSelected
          player={userInfo.favoritePlayer}
          team={userInfo.favoriteTeam}
        />
      </Layer>
    )
  }
}

// TODO: https://next.material-ui.com/demos/dialogs/#responsive-full-screen
// tslint:disable-next-line: no-any
export default withMobileDialog<Props>({ breakpoint: 'xs' })(UserLayer as any)
